import React, { useRef, useState, useEffect } from 'react';
import {
  BLINKS_REQUIRED,
  loadLandmarker,
  loadHandLandmarker,
  readFrame,
  createBlinkCounter,
  resetBlinkCounter,
  countBlink,
} from '../utils/faceLiveness';

// Longest side of the saved photo; the camera's own aspect ratio is kept.
const CAPTURE_WIDTH = 960;

const blinkPrompt = (blinks) =>
  blinks === 0
    ? 'Look at the camera and blink twice.'
    : `Good. Blink ${BLINKS_REQUIRED - blinks} more time${BLINKS_REQUIRED - blinks === 1 ? '' : 's'}.`;

const CameraCapture = ({ onPhotoCapture, buttonText = "Take Photo" }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const requestIdRef = useRef(0);
  const frameRef = useRef(null);
  const counterRef = useRef(createBlinkCounter());
  const frameCheckRef = useRef({ problem: 'The face check has not started yet.' });
  const shownRef = useRef('');
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("idle"); // idle, loading, checking, active, error
  const [liveness, setLiveness] = useState({ blinks: 0, message: '', problem: false });

  const stopWatching = () => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  };

  // Runs the face check on every new video frame until the camera closes.
  const watch = (landmarker, requestId, handLandmarker = null) => {
    const video = videoRef.current;
    let lastFrameTime = -1;
    // Hands are looked for on every other frame, which is enough to catch a
    // hand over the face and keeps slower phones smooth.
    let frameNo = 0;
    let hands = null;
    resetBlinkCounter(counterRef.current);
    frameCheckRef.current = { problem: 'The face check has not started yet.' };
    shownRef.current = '';

    const show = (blinks, message, problem) => {
      const key = `${blinks}|${problem}|${message}`;
      if (shownRef.current === key) return;
      shownRef.current = key;
      setLiveness({ blinks, message, problem });
    };

    const tick = (now) => {
      if (requestIdRef.current !== requestId || !videoRef.current) return;
      frameRef.current = requestAnimationFrame(tick);
      if (!video.videoWidth || video.currentTime === lastFrameTime) return;
      lastFrameTime = video.currentTime;

      let result;
      try { result = landmarker.detectForVideo(video, now); } catch { return; }
      if (handLandmarker && frameNo % 2 === 0) {
        try { hands = handLandmarker.detectForVideo(video, now)?.landmarks || null; } catch { hands = null; }
      }
      frameNo += 1;
      const frame = readFrame(result, hands);
      frameCheckRef.current = frame;
      const counter = counterRef.current;

      if (frame.problem) {
        // Any break in framing starts the blinks over, so they must come from
        // the person who is in the photo.
        resetBlinkCounter(counter);
        show(0, frame.problem, true);
        return;
      }
      if (counter.blinks < BLINKS_REQUIRED) countBlink(counter, frame.eyes, now);
      const blinks = Math.min(counter.blinks, BLINKS_REQUIRED);
      show(blinks, blinks >= BLINKS_REQUIRED ? 'Thank you. Now take the photo.' : blinkPrompt(blinks), false);
    };

    frameRef.current = requestAnimationFrame(tick);
  };

  const openCamera = () => {
    const requestId = (requestIdRef.current += 1);
    setError(null);
    setStatus("loading");
    setLiveness({ blinks: 0, message: '', problem: false });
    // Ensure the video element is rendered so the ref is available.
    // Set isCameraOpen early so JSX renders the <video> and the ref is attached.
    setIsCameraOpen(true);

    // Check basic API availability
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("Your browser doesn't support camera access. If the address does not begin with https, that is why.");
      setStatus("error");
      setIsCameraOpen(false);
      return;
    }

    // Stop any existing stream on the video element / ref
    stopWatching();
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(t => t.stop());
        videoRef.current.srcObject = null;
      }
    } catch (e) {
      console.warn('Error stopping previous tracks', e);
    }

    // A photo taken earlier no longer counts once the camera is opened again:
    // only a new photo that passes the check does.
    if (typeof onPhotoCapture === 'function') onPhotoCapture(null, null);
    setCapturedImage(null);

    // Load the face check (and the hand check beside it) while the camera starts.
    const checking = loadLandmarker().catch(err => {
      console.error('Face check failed to load:', err);
      return null;
    });
    const handChecking = loadHandLandmarker().catch(err => {
      console.warn('Hand check failed to load; the face check runs without it:', err);
      return null;
    });

    navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 960 } },
      audio: false,
    })
      .then(stream => {
        // If user closed camera before permissions resolved, stop immediately.
        if (requestIdRef.current !== requestId) {
          try { stream.getTracks().forEach(t => t.stop()); } catch {}
          return;
        }

        streamRef.current = stream;

        // Attach stream to the video element; retry briefly if ref isn't set yet
        const attach = () => {
          if (requestIdRef.current !== requestId) {
            try { stream.getTracks().forEach(t => t.stop()); } catch {}
            return;
          }

          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.onloadedmetadata = () => {
              if (requestIdRef.current !== requestId) {
                try { stream.getTracks().forEach(t => t.stop()); } catch {}
                if (videoRef.current) videoRef.current.srcObject = null;
                return;
              }

              videoRef.current.play()
                .then(() => {
                  setStatus('checking');
                  return checking;
                })
                .then(landmarker => Promise.resolve(handChecking).then(hands => [landmarker, hands]))
                .then(([landmarker, handLandmarker]) => {
                  if (requestIdRef.current !== requestId) return;
                  if (!landmarker) {
                    closeCamera();
                    setError('The face check could not be loaded. Check your internet connection and try again.');
                    setStatus('error');
                    return;
                  }
                  setStatus('active');
                  setError(null);
                  setLiveness({ blinks: 0, message: blinkPrompt(0), problem: false });
                  watch(landmarker, requestId, handLandmarker);
                })
                .catch(playError => {
                  console.error('Error playing video:', playError);
                  setError('Camera connected but unable to display. Please check your browser permissions.');
                  setStatus('error');
                });
            };
          } else {
            // If ref isn't yet attached to DOM, try again shortly
            setTimeout(attach, 50);
          }
        };

        attach();
      })
      .catch(err => {
        console.error('Camera access error:', err);
        const name = (err && err.name) || '';
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
          setError('Camera access denied. Please allow camera permissions in your browser settings.');
        } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          setError('No camera found. Please connect a camera and try again.');
        } else if (name === 'NotReadableError' || name === 'AbortError') {
          setError('The camera is being used by another program, such as a video call. Close it and try again.');
        } else {
          setError(`Camera error: ${err && err.message ? err.message : 'Unknown error'}`);
        }
        setStatus('error');
        setIsCameraOpen(false);
      });
  };

  // Simple close camera function
  const closeCamera = () => {
    // Invalidate any pending attach/play handlers
    requestIdRef.current += 1;
    stopWatching();

    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }
    } catch {}

    setIsCameraOpen(false);
    setStatus("idle");
  };

  const livenessPassed = status === 'active' && !liveness.problem && liveness.blinks >= BLINKS_REQUIRED;

  const capturePhoto = () => {
    try {
      if (!videoRef.current || !canvasRef.current || !videoRef.current.videoWidth) {
        setError("The camera is not ready yet. Give it a moment and try again.");
        return;
      }

      // Check again at the moment of capture, not only when the button lit up.
      const frame = frameCheckRef.current;
      if (frame.problem || counterRef.current.blinks < BLINKS_REQUIRED) {
        setLiveness({
          blinks: Math.min(counterRef.current.blinks, BLINKS_REQUIRED),
          message: frame.problem || blinkPrompt(counterRef.current.blinks),
          problem: true,
        });
        return;
      }

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const scale = Math.min(1, CAPTURE_WIDTH / video.videoWidth);
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);

      // Draw the video frame to canvas (unmirrored, unlike the preview)
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Get the data URL representing the image
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.88);
      setCapturedImage(imageDataUrl);
      // Immediately close the camera so the live preview stops right away
      closeCamera();

      // Convert to File object using the simplest approach (async)
      canvas.toBlob(blob => {
        if (blob) {
          const file = new File([blob], "client-photo.jpg", { type: "image/jpeg" });
          onPhotoCapture(file, imageDataUrl);
        } else {
          setError("Failed to capture photo. Please try again.");
        }
      }, 'image/jpeg', 0.88);
    } catch (err) {
      console.error("Error capturing photo:", err);
      setError(`Failed to capture photo: ${err.message || 'Unknown error'}`);
    }
  };

  // Ensure tracks are stopped if component unmounts while camera is open
  useEffect(() => {
    return () => {
      closeCamera();
    };
  }, []);

  const retryCamera = () => {
    setError(null);
    setTimeout(() => openCamera(), 100);
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    openCamera();
  };

  // Render UI based on current state
  return (
    <div className="w-full max-w-3xl mx-auto bg-white dark:bg-slate-800 p-3 rounded-lg border border-gray-200 dark:border-slate-700">
      <div className="mb-2">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Client Photo</h3>
        <p className="text-[11px] text-gray-600 dark:text-slate-400">Take a clear photo of yourself for verification holding your ID card. You will be asked to blink twice to confirm it is really you.</p>
      </div>

      {/* Status indicator */}
      {(status === "loading" || status === "checking") && (
        <div className="text-center mb-3">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {status === "loading" ? "Initializing Camera..." : "Loading the face check..."}
          </span>
        </div>
      )}

      {status === "active" && (
        <div className="text-center mb-3">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${livenessPassed ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
            <span className={`w-2 h-2 mr-1.5 rounded-full ${livenessPassed ? 'bg-green-500' : 'bg-blue-500 animate-pulse'}`}></span>
            {livenessPassed ? 'Liveness confirmed' : `Blinks: ${liveness.blinks} of ${BLINKS_REQUIRED}`}
          </span>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-3 p-3 bg-red-50 text-red-700 rounded-lg border border-red-200">
          <p className="mb-2">{error}</p>
          <button
            onClick={(e) => {
              e.preventDefault();
              retryCamera();
            }}
            type="button"
            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
          >
            Retry Camera
          </button>
        </div>
      )}

      {/* Display captured image */}
      {capturedImage ? (
        <div className="mb-4">
          <div className="relative border-2 border-gray-300 rounded-lg overflow-hidden">
            <img
              src={capturedImage}
              alt="Captured"
              className="w-full object-contain"
              style={{ maxHeight: "420px" }}
            />
            <button
              onClick={(e) => {
                e.preventDefault();
                retakePhoto();
              }}
              type="button"
              className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
              title="Retake Photo"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          <p className="text-center text-sm mt-2 text-green-600">Photo captured successfully</p>
        </div>
      ) : isCameraOpen ? (
        <div className="mb-4">
          <div className="relative border-2 border-gray-300 rounded-lg overflow-hidden">
            {/* Preview is mirrored so it behaves like a mirror; the saved photo is not */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-auto"
              style={{
                minHeight: '240px',
                maxHeight: '440px',
                objectFit: 'cover',
                backgroundColor: '#000',
                display: 'block',
                transform: 'scaleX(-1)'
              }}
            />

            {/* Capture button: enabled only once the blinks are seen */}
            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  capturePhoto();
                }}
                type="button"
                disabled={!livenessPassed}
                title={livenessPassed ? 'Take the photo' : `Blink ${BLINKS_REQUIRED} times first`}
                className="bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 shadow-md disabled:bg-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-400"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {/* Close button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                closeCamera();
              }}
              type="button"
              className="absolute top-2 right-2 bg-gray-700 text-white p-2 rounded-full hover:bg-gray-800 shadow-md"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* Live guidance from the face check */}
          {status === "active" && liveness.message && (
            <p
              role="status"
              className={`mt-2 p-2 text-sm rounded border ${
                liveness.problem
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : livenessPassed
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-blue-50 text-blue-800 border-blue-200'
              }`}
            >
              {liveness.message}
            </p>
          )}
        </div>
      ) : (
        <button
          onClick={(e) => {
            e.preventDefault();
            openCamera();
          }}
          type="button"
          className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg flex items-center justify-center space-x-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
          </svg>
          <span>{buttonText}</span>
        </button>
      )}

      {/* Hidden canvas for capturing */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default CameraCapture;
